import { parseToDate } from "../../../../utils/dateUtils";
import { translateClassName } from "../../../../utils/classroomUtils";

export const resolveClassroomId = (entry) => entry?.classroomId ?? entry?.id ?? entry?.classroom?.id ?? null;

export const resolveClassroomName = (classroom) =>
  classroom?.className ?? classroom?.classroom?.className ?? classroom?.classroom?.name ?? classroom?.name ?? "";

export const resolveTranslatedClassroomName = (classroom) => translateClassName(resolveClassroomName(classroom));

export const resolveTeacherName = (classroom) => {
  if (!classroom) return "";

  const teacher = classroom.teacher ?? classroom.classroom?.teacher ?? classroom.assignedTeacher ?? null;
  if (typeof teacher === "string") return teacher.trim();

  const firstName =
    teacher?.firstName ??
    teacher?.firstname ??
    classroom.teacherFirstName ??
    classroom.teacherFirstname ??
    classroom.firstNameTeacher ??
    "";

  const lastName =
    teacher?.lastName ??
    teacher?.lastname ??
    classroom.teacherLastName ??
    classroom.teacherLastname ??
    classroom.lastNameTeacher ??
    "";

  const composedName = [firstName, lastName].filter(Boolean).join(" ").trim();
  if (composedName) return composedName;

  return (
    classroom.teacherName ??
    classroom.classroom?.teacherName ??
    teacher?.name ??
    teacher?.fullName ??
    ""
  ).trim();
};

export const resolveCampusName = (classroom) => {
  if (!classroom) return "";

  const campus = classroom.campus ?? classroom.classroom?.campus ?? classroom.location ?? classroom.classroom?.location ?? null;
  if (typeof campus === "string") return campus.trim();

  return (
    campus?.name ??
    campus?.campusName ??
    classroom.campusName ??
    classroom.classroom?.campusName ??
    classroom.locationName ??
    classroom.classroom?.locationName ??
    ""
  ).trim();
};

const extractYear = (value) => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.getFullYear();
  }
  const date = parseToDate(value);
  return date ? date.getFullYear() : null;
};

export const formatSchoolYear = (classroom, fallback = "Year Not Found") => {
  if (classroom?.schoolYear) return classroom.schoolYear;
  const startYear = extractYear(classroom?.startDate);
  const endYear = extractYear(classroom?.endDate);
  if (startYear && endYear) return `${startYear} - ${endYear}`;
  if (startYear) return `${startYear}`;
  if (endYear) return `${endYear}`;
  return fallback;
};

export const normalizeClassroomEntry = (entry, childClassroomById = new Map()) => {
  const classroomId = resolveClassroomId(entry);
  const childClassroom = classroomId ? childClassroomById.get(String(classroomId)) : null;
  const startDate = parseToDate(entry?.startDate ?? childClassroom?.startDate);
  const endDate = parseToDate(entry?.endDate ?? childClassroom?.endDate);

  return {
    ...childClassroom,
    ...entry,
    id: classroomId,
    className:
      entry?.className ??
      entry?.classroom?.className ??
      entry?.classroom?.name ??
      childClassroom?.className ??
      childClassroom?.name,
    teacher: entry?.teacher ?? entry?.classroom?.teacher ?? childClassroom?.teacher ?? null,
    teacherId:
      entry?.teacherId ??
      entry?.teacher?.id ??
      entry?.classroom?.teacherId ??
      childClassroom?.teacherId ??
      childClassroom?.teacher?.id,
    teacherFirstName:
      entry?.teacherFirstName ??
      entry?.teacher?.firstName ??
      entry?.classroom?.teacherFirstName ??
      childClassroom?.teacherFirstName ??
      childClassroom?.teacher?.firstName,
    teacherLastName:
      entry?.teacherLastName ??
      entry?.teacher?.lastName ??
      entry?.classroom?.teacherLastName ??
      childClassroom?.teacherLastName ??
      childClassroom?.teacher?.lastName,
    schoolYear: entry?.schoolYear ?? childClassroom?.schoolYear,
    current: entry?.current ?? childClassroom?.current ?? false,
    startDate,
    endDate,
  };
};

export const categorizeChildClassrooms = (child, classroomHistory = [], today = new Date()) => {
  const pastClassrooms = [];
  const currentClassrooms = [];
  const futureClassrooms = [];
  const childClassrooms = Array.isArray(child?.classrooms) ? child.classrooms : [];
  const childClassroomById = new Map(
    childClassrooms
      .map((entry) => {
        const classroomId = resolveClassroomId(entry);
        return classroomId ? [String(classroomId), entry] : null;
      })
      .filter(Boolean)
  );

  const classroomEntries = classroomHistory.length > 0 ? classroomHistory : childClassrooms;
  classroomEntries.forEach((entry) => {
    const classroom = normalizeClassroomEntry(entry, childClassroomById);
    if (classroom.current) {
      currentClassrooms.push(classroom);
    } else if (classroom.endDate && classroom.endDate < today) {
      pastClassrooms.push(classroom);
    } else {
      futureClassrooms.push(classroom);
    }
  });

  return { pastClassrooms, currentClassrooms, futureClassrooms };
};

export const canNavigateToClassroom = (classroom) => Boolean(resolveClassroomId(classroom));

export const getClassroomDetailsPath = (classroom) => {
  const id = resolveClassroomId(classroom);
  return id ? `/school/classrooms/${id}/details` : null;
};
